import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { patientPortal } from '@/api/patientPortal';
import { toast } from 'sonner';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
  patientName?: string;
};

export function PatientEmailRegisterDialog({ open, onOpenChange, onDone, patientName }: Props) {
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const skip = () => {
    setEmail('');
    onOpenChange(false);
    onDone();
  };

  const save = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      skip();
      return;
    }
    setSaving(true);
    try {
      await patientPortal.updateEmail(trimmed);
      toast.success('Email saved to your profile');
      setEmail('');
      onOpenChange(false);
      onDone();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not save email');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : skip())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add your email (optional)</DialogTitle>
          <DialogDescription>
            {patientName ? (
              <>
                Hi <strong>{patientName}</strong>, we don&apos;t have an email on your profile yet.
                Add one to receive OTP and password reset links by email next time. You can skip this.
              </>
            ) : (
              <>
                Add an email to receive OTP and password reset links by email next time. You can skip
                this.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="patient-register-email">Email address</Label>
          <Input
            id="patient-register-email"
            type="email"
            className="mt-1"
            placeholder="you@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="ghost" onClick={skip} disabled={saving}>
            Skip for now
          </Button>
          <Button type="button" className="bg-teal-800 hover:bg-teal-900" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : email.trim() ? 'Save email' : 'Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
